<template>
  <div
    ref="filtersContainer"
    class="filter-container-stable flex flex-wrap gap-2 pb-4"
    role="list"
    :aria-label="$t('filter.filters_aria_label')"
  >
    <div role="listitem">
      <OdoscopeSearchSortingMenu :values="sortingValues" />
    </div>

    <div
      v-for="filter in displayedFilters"
      :key="filter.slug"
      class="filter-container-stable flex"
      role="listitem"
    >
      <LazyRangeFilter
        v-if="filter.type === 'range'"
        :id="filter.name"
        :filter="filter"
        :min-value="getUnfilteredRangeValues(filter)?.min"
        :max-value="getUnfilteredRangeValues(filter)?.max"
        :model-value="
          getPriceRange(
            getUnfilteredRangeValues(filter)?.min,
            getUnfilteredRangeValues(filter)?.max,
          )
        "
        :filter-values="getAvailableFilterValues(filter.slug)"
        @update:model-value="$emit('applyPriceFilter', $event)"
        @reset="$emit('resetPriceFilter')"
      />

      <!-- TODO: Add other filters if they ever get implemented in OSC -->

      <AttributesFilter
        v-else
        :id="filter.name"
        :filter="filter"
        :filter-values="getAvailableFilterValues(filter.slug)"
        :model-value="
          getFilterValuesForAppliedAttributeValues(
            filter.slug,
            appliedAttributeValues[filter.slug],
          )
        "
        @apply="$emit('applyAttributeFilter', filter.slug, $event.values)"
        @reset="$emit('resetFilter', filter.slug)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  CentAmount,
  ProductSearchQuery,
  RangeFilterItemWithValues,
} from '@scayle/storefront-nuxt'
import { getGroupedFilterableValues } from '@scayle/storefront-nuxt'
import type { OdoscopeFilter } from '@goldner-group/storefront-odoscope/types'
import type { RangeTuple } from '../ui/sliders/RangeSlider.vue'

interface Props {
  filters: OdoscopeFilter[]
  appliedFilter: ProductSearchQuery
  appliedBooleanValues: Record<string, boolean>
  appliedAttributeValues: Record<string, string[]>
  sortingValues: OdoscopeSortValue[]
  unfilteredFilters?: OdoscopeFilter[]
}

const props = defineProps<Props>()

defineEmits<{
  applyBooleanFilter: [slug: string, value: boolean]
  applyAttributeFilter: [slug: string, values: string[]]
  applyPriceFilter: [prices: RangeTuple]
  resetFilter: [slug: string]
  resetPriceFilter: []
}>()

const { filters: allAvailableFilters } = toRefs(props)

const filtersPresentationOrder: Record<string, number> = {
  isNew: 0,
  sale: 1,
  colorRange: 2,
  prices: 3,
  sizeCategorySize: 4,
  size: 5,
}

const getFilterPresentationPosition = (filterSlug: string) =>
  filtersPresentationOrder[filterSlug] ?? 100

const sortedFilters = computed(() => {
  return [...allAvailableFilters.value].sort(
    (a: OdoscopeFilter, b: OdoscopeFilter) =>
      getFilterPresentationPosition(a.slug) -
      getFilterPresentationPosition(b.slug),
  )
})

const displayedFilters = computed(() => sortedFilters.value)

const availableFilterValues = computed(() =>
  getGroupedFilterableValues(
    sortedFilters.value.map((f) => f.slug),
    sortedFilters.value,
  ),
)

const getAvailableFilterValues = (slug: string) =>
  availableFilterValues.value[slug] ?? []

const getFilterValuesForAppliedAttributeValues = (
  slug: string,
  values?: string[],
) => {
  const filter = availableFilterValues.value[slug]
  if (!filter || !values) {
    return []
  }
  return filter.filter((v: any) => values.includes(v.value.toString()))
}

/**
 *
 * @info Since OSC returns filtered prices, the range would be limited to already filtered values, which is why we need this function to get the unfiltered values.
 */
const getUnfilteredRangeValues = (filter: RangeFilterItemWithValues) => {
  if (!props.unfilteredFilters?.length) {
    return {
      min: filter.values[0].min,
      max: filter.values[0].max,
    }
  }

  const unfilteredRangeFilter = props.unfilteredFilters.find(
    (f) => f.slug === filter.slug && f.type === 'range',
  ) as RangeFilterItemWithValues

  if (unfilteredRangeFilter) {
    return {
      min: unfilteredRangeFilter.values[0].min,
      max: unfilteredRangeFilter.values[0].max,
    }
  } else {
    return {
      min: filter.values[0].min,
      max: filter.values[0].max,
    }
  }
}

const getPriceRange = (
  currentMin: CentAmount,
  currentMax: CentAmount,
): RangeTuple => {
  const appliedMin = props.appliedFilter.minPrice || currentMin
  const appliedMax = props.appliedFilter.maxPrice || currentMax
  const min = Math.max(currentMin, appliedMin, 0)
  const max = Math.min(currentMax, appliedMax)
  return [min, max]
}
</script>
